import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type CouponCreate,
  type CouponListParams,
  type CouponUpdate,
  createCoupon,
  deleteCoupon,
  downloadCouponsWithPrefix,
  getCouponById,
  getCoupons,
  updateCoupon,
} from "@/apis/coupon/coupon-apis";
import {
  type CouponUserListParams,
  getCouponUsers,
} from "@/apis/coupon/coupon-user-apis";
import { queryKeys } from "./keys";

export function useCoupons(params: CouponListParams) {
  return useQuery({
    queryKey: queryKeys.coupons.list(params),
    queryFn: () => getCoupons(params),
    placeholderData: keepPreviousData,
  });
}

export function useCoupon(id: number | null) {
  return useQuery({
    queryKey: queryKeys.coupons.detail(id ?? 0),
    // Query is disabled when id is null, so queryFn won't run with fallback value
    queryFn: () => getCouponById(id ?? 0),
    enabled: Boolean(id),
  });
}

export function useCreateCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CouponCreate) => createCoupon(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.coupons.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useUpdateCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: CouponUpdate }) =>
      updateCoupon(id, dto),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.coupons.detail(id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.coupons.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useDeleteCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteCoupon(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.coupons.detail(id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.coupons.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useDownloadCoupons() {
  return useMutation({
    mutationFn: (prefix: string) => downloadCouponsWithPrefix(prefix),
  });
}

export function useCouponUsers(params: CouponUserListParams) {
  return useQuery({
    queryKey: queryKeys.couponUsers.list(params.couponId, params),
    queryFn: () => getCouponUsers(params),
    placeholderData: keepPreviousData,
    enabled: Boolean(params.couponId),
  });
}
